import { type KeyboardEvent } from 'react';
import { ChevronRight } from 'lucide-react';
import { LiquidSurface } from '@repo/ui/components/liquid-surface';
import { cn } from '@repo/ui/lib/utils';

import type { HealthDriver } from '../utils/derive-health';
import { humanizeSlug } from '../utils/format';
import { MiniRing } from './portfolio-health-rings';

const DRIVER_STATE_CLASS: Record<HealthDriver['state'], string> = {
  sound: 'text-rowa-status-positive',
  watch: 'text-rowa-status-warning',
  'at-risk': 'text-rowa-status-error',
  gathering: 'text-rowa-text-tertiary',
  'awaiting-allocation': 'text-rowa-text-brand',
  'not-tracked': 'text-rowa-text-tertiary'
};

const CHECK_PIP_CLASS: Record<string, string> = {
  met: 'bg-rowa-status-positive',
  partially_met: 'bg-rowa-status-warning',
  not_met: 'bg-rowa-status-error'
};

const CHECK_PIP_LABEL: Record<string, string> = {
  met: 'looks good',
  partially_met: 'partly there',
  not_met: 'needs attention',
  not_evaluable: 'waiting on data'
};

/**
 * The card's third visual layer: one row per check. Quantitative checks
 * draw a bullet meter — the fill is today's reading, the tick is the
 * guideline — so an overshoot is visible before any number is read.
 */
function CheckMeterRow({
  check
}: {
  check: NonNullable<HealthDriver['checkStates']>[number];
}) {
  const scale =
    check.currentNum != null && check.guidelineNum != null
      ? Math.max(check.currentNum, check.guidelineNum) * 1.15
      : 0;
  // No scale, no meter: zero against zero would divide by zero and print
  // NaN% widths. The row then states the reading in words alone.
  const hasMeter = Number.isFinite(scale) && scale > 0;
  const percentOfScale = (value: number) =>
    Math.min(100, Math.max(0, (value / scale) * 100));
  const statusWords =
    CHECK_PIP_LABEL[check.status] ?? humanizeSlug(check.status).toLowerCase();
  const hasReading = check.current != null && check.guideline != null;

  return (
    <div
      className="flex items-center gap-2"
      title={
        hasReading
          ? `${check.longTitle}: ${check.current} today · guideline ${check.guideline}`
          : `${check.longTitle} · ${statusWords}`
      }
    >
      <span
        aria-hidden
        className={cn(
          'size-1.5 shrink-0 rounded-full',
          CHECK_PIP_CLASS[check.status] ??
            'border-rowa-text-primary/25 border bg-transparent'
        )}
      />
      <p className="text-rowa-label-sm text-rowa-text-secondary min-w-0 flex-1 truncate">
        {check.title}
      </p>
      {hasMeter && hasReading ? (
        <>
          <span
            role="img"
            aria-label={`${check.longTitle}: ${check.current} today, guideline ${check.guideline}`}
            className="bg-rowa-text-primary/10 relative h-1.5 w-12 shrink-0 overflow-visible rounded-full"
          >
            <span
              className={cn(
                'absolute inset-y-0 left-0 rounded-full',
                CHECK_PIP_CLASS[check.status] ?? 'bg-rowa-text-primary/25'
              )}
              style={{ width: `${percentOfScale(check.currentNum!)}%` }}
            />
            <span
              className="bg-rowa-text-primary/50 absolute -inset-y-0.5 w-[2px] rounded-[1px]"
              style={{ left: `${percentOfScale(check.guidelineNum!)}%` }}
            />
          </span>
          <span className="text-rowa-label-sm text-rowa-text-primary w-10 shrink-0 text-right font-mono tabular-nums">
            {check.current}
          </span>
        </>
      ) : (
        <span className="text-rowa-label-sm text-rowa-text-tertiary shrink-0">
          {hasReading ? check.current : statusWords}
        </span>
      )}
    </div>
  );
}

export function DriverTile({
  driver,
  onOpen
}: {
  driver: HealthDriver;
  onOpen?: () => void;
}) {
  const passing =
    driver.checkStates?.filter((check) => check.status === 'met').length ?? 0;

  return (
    <LiquidSurface
      type="inset"
      className={cn(
        'flex h-full min-w-0 items-start gap-4 p-4',
        onOpen &&
          'focus-visible:ring-rowa-border-accent cursor-pointer transition-transform outline-none focus-visible:ring-2 motion-safe:hover:-translate-y-0.5'
      )}
      data-driver={driver.id}
      {...(onOpen
        ? {
            role: 'button' as const,
            tabIndex: 0,
            'aria-label': `${driver.label}, view checks`,
            onClick: onOpen,
            onKeyDown: (event: KeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen();
              }
            }
          }
        : {})}
    >
      <MiniRing driver={driver} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-rowa-label-md text-rowa-text-primary">
            {driver.label}
          </p>
          {onOpen ? (
            <ChevronRight
              className="text-rowa-text-tertiary size-3.5 shrink-0"
              aria-hidden
            />
          ) : null}
        </div>
        <p
          className={cn(
            'text-rowa-label-sm mt-0.5 tracking-[0.08em]',
            DRIVER_STATE_CLASS[driver.state]
          )}
        >
          {driver.stateLabel}
        </p>
        {/* An unscored tile says why in one line above its check rows: the
            status word alone ("Waiting on data") is not what the backend
            said. Scored tiles let the rows speak. */}
        {driver.score == null &&
        driver.checkStates &&
        driver.checkStates.length > 0 ? (
          <p className="text-rowa-label-sm text-rowa-text-secondary mt-1">
            {driver.detail}
          </p>
        ) : null}
        {driver.checkStates && driver.checkStates.length > 0 ? (
          <div
            aria-label={`${passing} of ${driver.checkStates.length} checks passing`}
            className="mt-2.5 space-y-1.5"
          >
            {driver.checkStates.map((check) => (
              <CheckMeterRow key={check.id} check={check} />
            ))}
          </div>
        ) : (
          <p className="text-rowa-label-sm text-rowa-text-secondary mt-2">
            {driver.detail}
          </p>
        )}
      </div>
    </LiquidSurface>
  );
}
